import { useState, useEffect, useMemo } from 'react';
import type { Map as MapLibreMap, MapMouseEvent } from 'maplibre-gl';
import { MapStyleSelector } from './MapStyleSelector';
import { StreetViewPanel } from './StreetViewPanel';
import { MapExportButton } from './MapExportButton';
import { useGridsSafe, useUrbanNetworkSafe, useSyntaxAnalysisSafe } from '../spatial';
import { useRightPanelSafe } from '../layouts/panels/RightPanel/RightPanelContext';
import './map-controls.css';

/**
 * Props para MapControls
 */
interface MapControlsProps {
  /**
   * Instancia del mapa MapLibre
   */
  map: MapLibreMap | null;

  /**
   * Callback cuando cambia el modo de visualización
   */
  onViewModeChange?: (mode: '2D' | '3D') => void;

  /**
   * Callback cuando cambia el estilo del mapa
   */
  onStyleChange?: () => void;
}

/**
 * MapControls - Controles compactos personalizados para el mapa
 *
 * @version 1.1
 * @module core/map-engine
 *
 * @description
 * Controles de mapa personalizados siguiendo el sistema de diseño WESTEER.
 * Incluye zoom, brújula, toggle 2D/3D, y cambio de estilo.
 *
 * @remarks
 * **Características**:
 * - Glassmorphism adaptativo (funciona en mapas dark y light)
 * - Posición fixed en top-right
 * - Orientación horizontal
 * - Botones cuadrados compactos (36x36px)
 * - Iconos SVG inline
 * - Estados hover/active/focus accesibles
 * - Separadores sutiles entre grupos funcionales
 *
 * **Grupos de controles**:
 * 1. Zoom (+ / -)
 * 2. Brújula (reset bearing)
 * 3. Toggle 2D/3D
 * 4. Cambio de estilo de mapa
 *
 * @example
 * ```tsx
 * import { MapControls } from '@/core/map-engine';
 *
 * function MapView() {
 *   const [map, setMap] = useState<MapLibreMap | null>(null);
 *   const [viewMode, setViewMode] = useState<'2D' | '3D'>('2D');
 *
 *   return (
 *     <>
 *       <MapViewer onMapLoad={setMap} />
 *       <MapControls
 *         map={map}
 *         onViewModeChange={setViewMode}
 *         onStyleChange={() => console.log('Change style')}
 *       />
 *     </>
 *   );
 * }
 * ```
 */
export function MapControls({
  map,
  onViewModeChange,
  onStyleChange,
}: MapControlsProps): JSX.Element {
  // Hook del contexto de mallas (safe - puede ser null si no está en GridsProvider)
  const gridsContext = useGridsSafe();

  // Hook del contexto de red urbana (safe - puede ser null si no está en UrbanNetworkProvider)
  const urbanNetworkContext = useUrbanNetworkSafe();

  // Hook del panel derecho (safe - puede ser null si no está en RightPanelProvider)
  const rightPanelContext = useRightPanelSafe();

  // Obtener malla activa del contexto
  const activeGrid = useMemo(() => {
    if (!gridsContext) {
      // gridsContext null = MapControls renderizado fuera de GridsProvider
      console.warn('[MapControls] ⚠ gridsContext es null — MapControls no está dentro de GridsProvider. Botón hexágono deshabilitado.');
      return null;
    }
    if (!gridsContext.activeGridId) {
      // No hay malla con Power ON — botón hexágono deshabilitado (opacity 0.4)
      console.log('[MapControls] activeGridId es null — ninguna malla con Power ON. Botón hexágono: disabled.');
      return null;
    }
    const grid = gridsContext.grids.find(g => g.id === gridsContext.activeGridId) || null;
    if (!grid) {
      console.warn('[MapControls] ⚠ activeGridId', gridsContext.activeGridId, 'no encontrado en grids array (', gridsContext.grids.length, 'mallas). Botón hexágono: disabled.');
    } else {
      console.log('[MapControls] ✓ activeGrid:', grid.name, '— Botón hexágono: habilitado.');
    }
    return grid;
  }, [gridsContext]);

  // Estado de interactividad del contexto de mallas
  const isGridInteractive = gridsContext?.isGridInteractive || false;

  // Estado de red urbana
  const isNetworkActive = urbanNetworkContext?.isNetworkActive || false;
  const isNetworkInteractive = urbanNetworkContext?.isNetworkInteractive || false;
  const [viewMode, setViewMode] = useState<'2D' | '3D'>('2D');
  const [isStyleSelectorOpen, setIsStyleSelectorOpen] = useState(false);
  const [isStreetViewOpen, setIsStreetViewOpen] = useState(false);
  const [isStreetViewSelectionMode, setIsStreetViewSelectionMode] = useState(false);
  const [streetViewLocation, setStreetViewLocation] = useState({ lat: 0, lng: 0 });

  /**
   * Zoom in
   */
  const handleZoomIn = (): void => {
    if (map) {
      map.zoomIn();
    }
  };

  /**
   * Zoom out
   */
  const handleZoomOut = (): void => {
    if (map) {
      map.zoomOut();
    }
  };

  /**
   * Reset bearing (orientación de la brújula)
   */
  const handleResetBearing = (): void => {
    if (map) {
      map.resetNorth();
    }
  };

  /**
   * Toggle 2D/3D
   */
  const handleToggleViewMode = (): void => {
    const newMode = viewMode === '2D' ? '3D' : '2D';
    setViewMode(newMode);

    if (map) {
      if (newMode === '3D') {
        map.easeTo({ pitch: 60 });
      } else {
        map.easeTo({ pitch: 0 });
      }
    }

    onViewModeChange?.(newMode);
  };

  /**
   * Abrir selector de estilos
   */
  const handleOpenStyleSelector = (): void => {
    setIsStyleSelectorOpen(true);
  };

  /**
   * Toggle modo de selección de Street View
   */
  const handleToggleStreetViewMode = (): void => {
    setIsStreetViewSelectionMode((prev) => !prev);
  };

  /**
   * Toggle interactividad de malla activa
   */
  const handleToggleGridInteractivity = (): void => {
    if (!gridsContext) return;

    const newInteractiveState = !isGridInteractive;
    gridsContext.setIsGridInteractive(newInteractiveState);

    // Mutex: activar H3 apaga seleccionabilidad de Red Urbana (solo al activar)
    if (newInteractiveState && urbanNetworkContext) {
      urbanNetworkContext.setIsNetworkInteractive(false);
    }

    // Abrir panel derecho con listado de celdas al activar interactividad
    // (solo si RightPanelContext está disponible)
    if (rightPanelContext) {
      if (newInteractiveState) {
        rightPanelContext.setActivePanel('grid-cells');
      } else {
        // Cerrar panel al desactivar interactividad
        rightPanelContext.setActivePanel(null);
      }
    }
  };

  /**
   * Toggle interactividad de red urbana
   */
  const handleToggleNetworkInteractivity = (): void => {
    if (!urbanNetworkContext) return;

    const newInteractiveState = !isNetworkInteractive;
    urbanNetworkContext.setIsNetworkInteractive(newInteractiveState);

    // Mutex: activar Red apaga seleccionabilidad de H3 (solo al activar)
    if (newInteractiveState && gridsContext) {
      gridsContext.setIsGridInteractive(false);
    }

    // El RightPanel de segmentos se abre automáticamente cuando el usuario
    // hace clic en un edge (vía selectedEdgeId en MapLayout).
    if (rightPanelContext && !newInteractiveState) {
      rightPanelContext.setActivePanel(null);
    }
  };

  /**
   * Escucha clics en el mapa cuando está en modo selección de Street View
   */
  useEffect(() => {
    if (!map || !isStreetViewSelectionMode) return;

    // Cambiar cursor a crosshair
    const canvas = map.getCanvas();
    const originalCursor = canvas.style.cursor;
    canvas.style.cursor = 'crosshair';

    // Handler para clic en mapa
    const handleMapClick = (e: MapMouseEvent): void => {
      const { lng, lat } = e.lngLat;
      setStreetViewLocation({ lat, lng });
      setIsStreetViewOpen(true);
      setIsStreetViewSelectionMode(false); // Salir del modo selección
    };

    map.on('click', handleMapClick);

    return () => {
      canvas.style.cursor = originalCursor;
      map.off('click', handleMapClick);
    };
  }, [map, isStreetViewSelectionMode]);

  /**
   * Permite cancelar el modo selección con tecla ESC
   */
  useEffect(() => {
    if (!isStreetViewSelectionMode) return;

    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsStreetViewSelectionMode(false);
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isStreetViewSelectionMode]);

  // Estado de carga de red — para NetworkSyncBanner (puede ser null fuera de UrbanNetworkProvider)
  const loadingNetwork = urbanNetworkContext?.loadingNetwork ?? false;
  const previewNetworkActive = urbanNetworkContext?.previewNetworkActive ?? false;

  // Estado de carga de sintaxis — para SyntaxLoadingBanner (null fuera de SyntaxAnalysisProvider)
  const syntaxContext = useSyntaxAnalysisSafe();
  const syntaxLoading = (syntaxContext?.syntaxModeActive === true) && (syntaxContext?.status === 'loading');

  return (
    <>
    <div className="map-controls-dock">
      <div className="map-controls">
        {/* Grupo: Zoom */}
        <div className="map-controls-group">
          <button
            className="map-control-btn"
            onClick={handleZoomIn}
            aria-label="Zoom in"
            title="Zoom in"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="8" y1="4" x2="8" y2="12" />
              <line x1="4" y1="8" x2="12" y2="8" />
            </svg>
          </button>

          <div className="map-controls-separator" />

          <button
            className="map-control-btn"
            onClick={handleZoomOut}
            aria-label="Zoom out"
            title="Zoom out"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="4" y1="8" x2="12" y2="8" />
            </svg>
          </button>
        </div>

        {/* Separador de grupos */}
        <div className="map-controls-group-separator" />

        {/* Grupo: Brújula */}
        <div className="map-controls-group">
          <button
            className="map-control-btn"
            onClick={handleResetBearing}
            aria-label="Reset north"
            title="Reset north"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="8,2 11,10 8,8 5,10" fill="currentColor" />
              <circle cx="8" cy="8" r="6" fill="none" />
            </svg>
          </button>
        </div>

        {/* Separador de grupos */}
        <div className="map-controls-group-separator" />

        {/* Grupo: 2D/3D Toggle */}
        <div className="map-controls-group">
          <button
            className={`map-control-btn ${viewMode === '3D' ? 'active' : ''}`}
            onClick={handleToggleViewMode}
            aria-label={`Switch to ${viewMode === '2D' ? '3D' : '2D'} view`}
            title={`Switch to ${viewMode === '2D' ? '3D' : '2D'} view`}
          >
            <span className="map-control-text">{viewMode}</span>
          </button>
        </div>

        {/* Separador de grupos */}
        <div className="map-controls-group-separator" />

        {/* Grupo: Cambio de estilo */}
        <div className="map-controls-group">
          <button
            className="map-control-btn"
            onClick={handleOpenStyleSelector}
            aria-label="Change map style"
            title="Change map style"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="2" width="12" height="12" rx="2" />
              <path d="M2 6 L14 6" />
              <path d="M6 2 L6 14" />
            </svg>
          </button>
        </div>

        {/* Separador de grupos */}
        <div className="map-controls-group-separator" />

        {/* Grupo: Street View */}
        <div className="map-controls-group">
          <button
            className={`map-control-btn ${isStreetViewSelectionMode ? 'active' : ''}`}
            onClick={handleToggleStreetViewMode}
            aria-label={
              isStreetViewSelectionMode
                ? 'Cancelar selección de Street View'
                : 'Activar Street View - Haz clic en el mapa'
            }
            title={
              isStreetViewSelectionMode
                ? 'Cancelar selección (ESC)'
                : 'Haz clic en el mapa para ver Street View'
            }
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="8" cy="6" r="2" />
              <path d="M8 8 L8 14" />
              <circle cx="8" cy="8" r="6" />
            </svg>
          </button>
        </div>

        {/* Separador de grupos */}
        <div className="map-controls-group-separator" />

        {/* Grupo: Exportación */}
        <div className="map-controls-group">
          <MapExportButton />
        </div>

        {/* Separador de grupos */}
        <div className="map-controls-group-separator" />

        {/* Grupo: Interactividad de Mallas */}
        <div className="map-controls-group">
          <button
            className={`map-control-btn ${isGridInteractive ? 'active' : ''}`}
            onClick={handleToggleGridInteractivity}
            disabled={!activeGrid}
            aria-label={
              !activeGrid
                ? 'No hay malla activa'
                : isGridInteractive
                ? `Desactivar interactividad de ${activeGrid.name}`
                : `Activar interactividad de ${activeGrid.name}`
            }
            title={
              !activeGrid
                ? 'Activa una malla primero'
                : isGridInteractive
                ? `Interactividad activa: ${activeGrid.name}`
                : `Activar clicks en celdas: ${activeGrid.name}`
            }
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {/* Icono de hexágono H3 regular (flat-topped) */}
              <polygon points="8,2.5 12.8,5.2 12.8,10.8 8,13.5 3.2,10.8 3.2,5.2" />
            </svg>
          </button>
        </div>

        {/* Separador de grupos */}
        {isNetworkActive && <div className="map-controls-group-separator" />}

        {/* Grupo: Interactividad de Red Urbana (solo visible si Power está ON) */}
        {isNetworkActive && (
          <div className="map-controls-group">
            <button
              className={`map-control-btn ${isNetworkInteractive ? 'active' : ''}`}
              onClick={handleToggleNetworkInteractivity}
              aria-label={
                isNetworkInteractive
                  ? 'Desactivar interactividad de red urbana'
                  : 'Activar interactividad de red urbana'
              }
              title={
                isNetworkInteractive
                  ? 'Interactividad activa: Red Urbana'
                  : 'Activar clicks en segmentos de calle'
              }
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {/* Icono de red/network (líneas conectadas) */}
                <circle cx="3" cy="3" r="1.5" fill="currentColor" />
                <circle cx="13" cy="3" r="1.5" fill="currentColor" />
                <circle cx="8" cy="8" r="1.5" fill="currentColor" />
                <circle cx="3" cy="13" r="1.5" fill="currentColor" />
                <circle cx="13" cy="13" r="1.5" fill="currentColor" />
                <line x1="4.2" y1="3.5" x2="6.8" y2="7.2" />
                <line x1="9.2" y1="7.2" x2="11.8" y2="3.5" />
                <line x1="4.2" y1="12.5" x2="6.8" y2="8.8" />
                <line x1="9.2" y1="8.8" x2="11.8" y2="12.5" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* NetworkSyncBanner: aviso post-SPIKE mientras la red completa sigue cargando */}
      {previewNetworkActive && loadingNetwork && (
        <div className="nsb" role="status" aria-live="polite">
          <span className="nsb__dot" aria-hidden="true" />
          <span className="nsb__label">Cargando red urbana</span>
        </div>
      )}

      {/* SyntaxLoadingBanner: aviso mientras las métricas de sintaxis se cargan */}
      {syntaxLoading && (
        <div className="nsb" role="status" aria-live="polite">
          <span className="nsb__dot" aria-hidden="true" />
          <div className="nsb__content">
            <span className="nsb__label">Cargando sintaxis espacial</span>
            <span className="nsb__sublabel">Preparando métricas de red</span>
          </div>
        </div>
      )}

      {/* MapStyleSelector usa createPortal — puede vivir aquí sin problemas */}
      <MapStyleSelector
        map={map}
        isOpen={isStyleSelectorOpen}
        onClose={() => setIsStyleSelectorOpen(false)}
        onStyleChange={onStyleChange}
      />
    </div>

    {/* StreetViewPanel fuera del dock: no usa portal, necesita pointer-events propios */}
    <StreetViewPanel
      isOpen={isStreetViewOpen}
      onClose={() => setIsStreetViewOpen(false)}
      lat={streetViewLocation.lat}
      lng={streetViewLocation.lng}
    />
    </>
  );
}
